/**
 * Call creation, persistence and retrieval.
 *
 * Jeff must work when the backend does not. Every call is written to
 * localStorage first and pushed to the Worker second; if D1 is not configured,
 * the Worker is unreachable, or the browser is offline, the call still exists,
 * the result screen still renders, and `/call/{id}` still resolves for the
 * person who made it. Only cross-device sharing needs the server.
 */

import type { CreateCallInput, Direction, JeffCall, RecordStats } from '../../shared/types.ts';
import { generateCallId } from '../../shared/ids.ts';
import { JEFF } from '../config/jeff.ts';

const STORAGE_KEY = 'jeff.trade:calls:v1';
const LOCAL_LIMIT = 250;

/** Where a call ultimately lives. Surfaced in the UI so nothing is pretended. */
export type PersistenceMode = 'global' | 'local';

export interface ResolvedCall {
  call: JeffCall;
  mode: PersistenceMode;
}

export interface NewCallFields {
  direction: Direction;
  entropy: string | null;
  /**
   * The market Jeff named. Drawn from `shared/assets.ts` by the caller, and
   * re-checked against that same list by the Worker — the client is trusted to
   * pick, never to invent.
   */
  asset?: string | null;
}

/**
 * Mints a call. Does not persist it.
 *
 * There is still nothing to collect from the user — the market is drawn, not
 * asked for. `price` stays null here on purpose: the entry is whatever the Worker
 * observes when the call is written, because a client-supplied entry price is a
 * client-supplied win rate. `context` is only ever set by the offline importer.
 */
export function createCall(fields: NewCallFields): JeffCall {
  return {
    id: generateCallId(),
    asset: fields.asset ?? null,
    price: null,
    context: null,
    direction: fields.direction,
    createdAt: Date.now(),
    jeffVersion: JEFF.jeffVersion,
    status: 'PENDING',
    resolvedAt: null,
    resultNotes: null,
    entropy: fields.entropy,
  };
}

// ── Local store ─────────────────────────────────────────────────────────────

function readStore(): JeffCall[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isJeffCall);
  } catch {
    // Private browsing, a full quota, or hand-edited JSON. Behave as if empty.
    return [];
  }
}

function writeStore(calls: JeffCall[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(calls.slice(0, LOCAL_LIMIT)));
  } catch {
    // Persistence is best-effort; the in-memory call is still valid.
  }
}

function isJeffCall(value: unknown): value is JeffCall {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    // Null for a call made before Jeff named markets, or with assets disabled.
    (v.asset === null || v.asset === undefined || typeof v.asset === 'string') &&
    (v.direction === 'LONG' || v.direction === 'SHORT') &&
    typeof v.createdAt === 'number'
  );
}

export function saveLocalCall(call: JeffCall): void {
  const existing = readStore().filter((c) => c.id !== call.id);
  writeStore([call, ...existing]);
}

export function listLocalCalls(): JeffCall[] {
  return readStore().sort((a, b) => b.createdAt - a.createdAt);
}

export function getLocalCall(id: string): JeffCall | null {
  return readStore().find((c) => c.id === id) ?? null;
}

export function clearLocalCalls(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

// ── Server ──────────────────────────────────────────────────────────────────

function toInput(call: JeffCall): CreateCallInput {
  return {
    id: call.id,
    asset: call.asset,
    price: call.price,
    context: call.context,
    direction: call.direction,
    createdAt: call.createdAt,
    jeffVersion: call.jeffVersion,
    entropy: call.entropy,
  };
}

/**
 * Persists a call. Always writes locally; attempts the server when enabled.
 * Returns where it actually ended up so the share panel can say so honestly.
 */
export async function persistCall(call: JeffCall, signal?: AbortSignal): Promise<PersistenceMode> {
  saveLocalCall(call);
  if (!JEFF.enableD1) return 'local';

  try {
    const response = await fetch('/api/calls', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(toInput(call)),
      signal: signal ?? null,
    });
    // 501 is the Worker telling us D1 is not bound. Anything else non-2xx is a
    // genuine failure, and both degrade to the same place.
    return response.ok ? 'global' : 'local';
  } catch {
    return 'local';
  }
}

/** Looks up a call: server first (so shared links work), then this browser. */
export async function fetchCall(id: string, signal?: AbortSignal): Promise<ResolvedCall | null> {
  if (JEFF.enableD1) {
    try {
      const response = await fetch(`/api/calls/${encodeURIComponent(id)}`, { signal: signal ?? null });
      if (response.ok) {
        const call = (await response.json()) as JeffCall;
        if (isJeffCall(call)) {
          saveLocalCall(call);
          return { call, mode: 'global' };
        }
      }
    } catch {
      // fall through to local
    }
  }

  const local = getLocalCall(id);
  return local ? { call: local, mode: 'local' } : null;
}

/** Track-record numbers, global when available and local otherwise. */
export async function fetchStats(signal?: AbortSignal): Promise<RecordStats> {
  if (JEFF.enableD1) {
    try {
      const response = await fetch('/api/stats', { signal: signal ?? null });
      if (response.ok) {
        const stats = (await response.json()) as RecordStats;
        if (typeof stats?.totalCalls === 'number') return stats;
      }
    } catch {
      // fall through to local
    }
  }
  return computeLocalStats(listLocalCalls());
}

/**
 * The same aggregation the Worker performs, applied to whatever this browser
 * has. Kept here rather than duplicated in a component so the two code paths
 * cannot disagree about what "current streak" means.
 *
 * The resolved-block gate below counts VOIDs deliberately, matching
 * `worker/routes/stats.ts`. Gating on WIN/LOSS alone looked equivalent and was
 * not: a store holding only voided calls would show the block globally and hide
 * it locally. `summariseResolved` reports a null win rate when nothing is
 * decided, so showing the block on voids alone states no unearned number.
 */
export function computeLocalStats(calls: JeffCall[]): RecordStats {
  const total = calls.length;
  const longCalls = calls.filter((c) => c.direction === 'LONG').length;
  const resolvedCalls = calls.filter(
    (c) => c.status === 'WIN' || c.status === 'LOSS' || c.status === 'VOID',
  );

  const timestamps = calls.map((c) => c.createdAt);

  return {
    totalCalls: total,
    longCalls,
    shortCalls: total - longCalls,
    resolved: resolvedCalls.length > 0 ? summariseResolved(calls) : null,
    firstCallAt: timestamps.length ? Math.min(...timestamps) : null,
    lastCallAt: timestamps.length ? Math.max(...timestamps) : null,
    global: false,
  };
}

/**
 * Only ever called once real outcomes exist. Until then the record page shows
 * volume and nothing else — an unverified win rate would be a fabrication.
 */
export function summariseResolved(calls: JeffCall[]): NonNullable<RecordStats['resolved']> {
  const chronological = [...calls].sort((a, b) => a.createdAt - b.createdAt);

  let wins = 0;
  let losses = 0;
  let voids = 0;
  let pending = 0;
  let currentStreak = 0;
  let longestWinStreak = 0;
  let running = 0;

  for (const call of chronological) {
    switch (call.status) {
      case 'WIN':
        wins++;
        running = running >= 0 ? running + 1 : 1;
        longestWinStreak = Math.max(longestWinStreak, running);
        break;
      case 'LOSS':
        losses++;
        running = running <= 0 ? running - 1 : -1;
        break;
      case 'VOID':
        voids++;
        break; // voids neither break nor extend a streak
      default:
        pending++;
        break;
    }
    if (call.status === 'WIN' || call.status === 'LOSS') currentStreak = running;
  }

  const decided = wins + losses;
  return {
    wins,
    losses,
    voids,
    pending,
    winRate: decided > 0 ? wins / decided : null,
    currentStreak,
    longestWinStreak,
  };
}
